import type { MutiaraJenis, Rasa } from '@iqrapath/shared'
import { useQuery } from '@tanstack/react-query'
import type { ObjId } from '../../components/Obj.tsx'
import { api } from '../../lib/api.ts'
import type { Passage } from '../pegangan/pegangan.ts'

/** Mirrors apps/api/src/services/mutiara.ts. Every card is a view on a verified dataset (never AI-written). */
type Base = { id: string; read: boolean; saved: boolean }
export type AyatCard = Base & { jenis: 'ayat'; ayat: Passage; tafsir: string }
export type HadisCard = Base & {
  jenis: 'hadis'
  hadisId: string
  title: string
  text: string
  ar: string | null
  attribution: string
  grade: string
  faedah: string | null
  url: string
  source: string
}
export type KataCard = Base & {
  jenis: 'kata'
  ar: string
  arti: string
  latin: string | null
  audio: string | null
  count: number
  contoh: { ref: string; words: string[]; at: number; terjemah: string; audio: string | null; url: string; wordsUrl: string }
}
export type PeganganMini = Base & { jenis: 'pegangan'; slug: string; rasa: Rasa; judul: string; ringkas: string; ayat: Passage; langkah: string; reviewed: boolean; ai: boolean }
export type MutiaraCard = AyatCard | HadisCard | KataCard | PeganganMini

export type Deck = { ok: true; today: string; limit: number; readToday: number; full: boolean; cards: MutiaraCard[] }

/** Label, object and a calm gradient per kind (docs/UX-PLAYBOOK.md §5 "gradien per pilar"). */
export const JENIS_INFO: Record<MutiaraJenis, { label: string; obj: ObjId; tint: [string, string] }> = {
  ayat: { label: 'Satu Ayat', obj: 'mushaf', tint: ['#E2F3EF', '#C9E6DE'] },
  hadis: { label: 'Hadis', obj: 'kendi', tint: ['#FBEFD6', '#F1DCAE'] },
  kata: { label: 'Satu Kata', obj: 'buku', tint: ['#E7EAF6', '#CFD6EE'] },
  pegangan: { label: 'Pegangan', obj: 'embun', tint: ['#F6E8DE', '#EBCFBC'] },
}
export const jenisTint = (j: MutiaraJenis) => `linear-gradient(165deg, ${JENIS_INFO[j].tint[0]}, ${JENIS_INFO[j].tint[1]})`

export const DECK_KEY = (jenis?: MutiaraJenis) => ['mutiara', 'deck', jenis ?? 'semua'] as const
export const useDeck = (jenis?: MutiaraJenis) =>
  useQuery({ queryKey: DECK_KEY(jenis), queryFn: () => api<Deck>('GET', `/api/mutiara${jenis ? `?jenis=${jenis}` : ''}`), staleTime: 5 * 60_000 })

export const useKotak = () =>
  useQuery({ queryKey: ['mutiara', 'kotak'], queryFn: () => api<{ ok: true; cards: MutiaraCard[] }>('GET', '/api/mutiara/kotak') })

export const postRead = (id: string) => api<{ ok: true; readToday: number; full: boolean; lit: boolean }>('POST', '/api/mutiara/read', { id })
export const postSaved = (id: string, on: boolean) => api<{ ok: true; saved: boolean }>('POST', '/api/mutiara/simpan', { id, on })

/** Text for "Kirim": what the card quotes, its source, and where it came from. */
export function shareTextOf(c: MutiaraCard): { title: string; text: string } {
  switch (c.jenis) {
    case 'ayat': {
      const tr = c.ayat.verses?.map((v) => v.terjemah).join(' ') ?? ''
      return { title: c.ayat.ref, text: `${tr}\n(${c.ayat.ref})\n\nDibagikan dari IqraPath` }
    }
    case 'hadis':
      return { title: c.title, text: `${c.text}\n(${c.attribution}; ${c.grade})\n${c.url}\n\nDibagikan dari IqraPath` }
    case 'kata':
      return { title: c.arti, text: `${c.ar} (${c.latin ?? ''}) artinya "${c.arti}".\n\n${c.contoh.terjemah}\n(${c.contoh.ref})\n\nDibagikan dari IqraPath` }
    case 'pegangan': {
      const tr = c.ayat.verses?.map((v) => v.terjemah).join(' ') ?? ''
      return { title: c.judul, text: `${c.judul}\n\n${tr}\n(${c.ayat.ref})\n\n${c.langkah}\n\nDibagikan dari IqraPath` }
    }
  }
}
